// src/app/services/auth.service.ts
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, catchError, combineLatest, filter, map, Observable, of, startWith, take, tap } from 'rxjs';
import { VrcBrowserStorageService } from './vrc-browser-storage.service';
import { VrcDataService } from './vrc-data.service';
import { VrcStorageService } from './vrc-storage.service';

@Injectable()
export class VrcAuthService {
  private _router = inject(Router);
  private _authService = inject(MsalService);
  private _bst = inject(VrcBrowserStorageService);
  private _broadcastService = inject(MsalBroadcastService);
  private _st = inject(VrcStorageService);
  private _srvc = inject(VrcDataService);
  private _msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);

  private _isLoggedIn = new BehaviorSubject<boolean>(false);
  public isLoggedIn$: Observable<boolean> = this._isLoggedIn.asObservable();
  private _iscustomLoggedIn = new BehaviorSubject<{ status: boolean, eventType: EventType | null }>({ status: false, eventType: null });
  public isCustomLoggedIn$: Observable<{ status: boolean, eventType: EventType | null }> = this._iscustomLoggedIn.asObservable();
  private _msalIsInitiazed = new BehaviorSubject<boolean>(false);
  public msalIsInitiazed = false;
  public msalIsInitiazedObservable: Observable<boolean> = this._msalIsInitiazed.asObservable();

  private _msalIsLoggedIn = new BehaviorSubject<boolean>(false);
  public msalIsLoggedIn: Observable<boolean> = this._msalIsLoggedIn.asObservable();

  constructor() {
    this.getCustomLoginStatus();
    combineLatest([
      this.getCustomLoginStatus(),
      this._broadcastService.msalSubject$.pipe(
        filter(event =>
          event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.LOGOUT_SUCCESS ||
          event.eventType === EventType.INITIALIZE_END
        ),
        map(x => {
          this.checkMsalLoginStatus();
          if (x.eventType === EventType.LOGIN_SUCCESS) {
            return { status: ((<any>this._authService.instance).controller.initialized ? this._authService.instance.getAllAccounts().length > 0 : false), eventType: x.eventType };
          } else if (x.eventType === EventType.LOGOUT_SUCCESS) {
            return { status: false, eventType: x.eventType };
          } else {
            this._msalIsInitiazed.next(true);
            return { status: false, eventType: x.eventType };
          }
        }),
        startWith({ status: ((<any>this._authService.instance).controller.initialized ? this._authService.instance.getAllAccounts().length > 0 : false), eventType: null })
      )
    ]).pipe(
      map(([customLoggedIn, msalLoggedIn]) => {
        return msalLoggedIn.status ? msalLoggedIn : customLoggedIn;
      }),
      tap(x => {
        this.setLogedInStatus(x.status);
        if (x.eventType === EventType.LOGIN_SUCCESS) {
          this._bst.deleteToken();
          this._bst.deleteRefreshToken();
          this._router.navigate([this._st.returnRoute]);
        } else if (x.eventType === EventType.LOGOUT_SUCCESS) {
          this._router.navigate(['login']);
        }
      })
    ).subscribe();
    this._authService.handleRedirectObservable().pipe(
      tap(() => {
        this.checkMsalLoginStatus();
      }),
      catchError(x => {
        console.error(x);
        return of(null);
      })).subscribe();
    // this._broadcastService.msalSubject$
    //   .pipe(
    //     filter((event: EventMessage) =>
    //       event.eventType === EventType.LOGIN_SUCCESS ||
    //       event.eventType === EventType.LOGOUT_SUCCESS ||
    //       event.eventType === EventType.INITIALIZE_END
    //     ),
    //     tap(x => {
    //       this.checkMsalLoginStatus();
    //       if (x.eventType === EventType.LOGIN_SUCCESS) {
    //         this._bst.deleteToken();
    //         this._bst.deleteRefreshToken();
    //         this._router.navigate([this._st.returnRoute]);
    //       } else if (x.eventType === EventType.LOGOUT_SUCCESS) {
    //         this._router.navigate(['login']);
    //       } else {
    //         this._msalIsInitiazed.next(true);
    //       }
    //     })
    //   )
    //   .subscribe();
    this._broadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        tap(() => {
          this.msalIsInitiazed = true;
          this._msalIsInitiazed.next(true);
        }),
        catchError(err => {
          this._router.navigate(['login']);
          return err;
        }),
      ).subscribe();

    // this._authService.instance.getConfiguration().auth.redirectUri = location.origin;
  }

  loginPopup(): void {
    this._bst.clearAuthentication();
    if (this._msalGuardConfig.authRequest) {
      this._authService
        .loginPopup({ ...this._msalGuardConfig.authRequest } as PopupRequest)
        .pipe(tap(result => {
          this._authService.instance.setActiveAccount(result.account);
        })).subscribe();
    } else {
      this._authService
        .loginPopup().pipe(tap(result => { this._authService.instance.setActiveAccount(result.account); })).subscribe();
    }
  }
  loginRedirect() {
    if (this._msalGuardConfig.authRequest) {
      this._authService.loginRedirect({
        ...this._msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this._authService.loginRedirect();
    }
  }
  get IsAzureUser(): boolean {
    return (<any>this._authService.instance).controller.initialized ? this._authService.instance.getAllAccounts().length > 0 : false;
  }

  checkMsalLoginStatus(): void {
    const accounts = this._authService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this._authService.instance.setActiveAccount(accounts[0]);
    }
  }

  setLogedInStatus(status: boolean): void {
    this._isLoggedIn.next(status);
  }

  getLogedInStatus(): boolean {
    return this._isLoggedIn.getValue();
  }

  getMsalAccount(): AccountInfo | null {
    return this._authService.instance.getActiveAccount();
  }

  loginWithCustom(userName: string, password: string, returnRoute: string | null): Observable<boolean> {
    return this._srvc.getUserToken({ userName: userName, password: password }).pipe(
      take(1),
      map(x => {
        this._bst.token = x.token
        this._bst.refreshToken = x.refreshToken
        this._bst.currentUser;
        this._iscustomLoggedIn.next({ status: true, eventType: null });
        this._router.navigate([returnRoute]);
        return true;
      }),
      catchError((err: HttpErrorResponse) => {
        this.logout(null);
        return of(false);
      }));
  }
  loginWithRefreshToken(returnRoute: string | null): Observable<boolean> {
    return this._srvc.refreshToken().pipe(
      take(1),
      map(x => {
        this._bst.token = x.token
        this._bst.refreshToken = x.refreshToken
        this._bst.currentUser;
        this._iscustomLoggedIn.next({ status: true, eventType: null });
        this._router.navigate([returnRoute]);
        return true;
      }),
      catchError((err: HttpErrorResponse) => {
        this._bst.clearAuthentication();
        return of(false);
      }));
  }

  loginWithNiceCustom(sessionId: string, agentId: number, contactId: number, returnRoute: string | null): Observable<boolean> {
    return this._srvc.getToken({ sessionId: sessionId, agentId: agentId, contactId: contactId }).pipe(
      take(1),
      map(x => {
        this._bst.token = x.token;
        this._bst.refreshToken = x.refreshToken;
        this._bst.currentUser;
        this._bst.subscription = this._bst.subscriptionFromToken;
        this._iscustomLoggedIn.next({ status: true, eventType: null });
        if (returnRoute) {
          this._router.navigate([returnRoute]);
        }
        return true;
      }));
  }
  getUserRoles(): string[] {
    if (this.getCustomAccessToken()) {
      return this._bst.roles || [];
    }

    const account = this._authService.instance.getActiveAccount() || this._authService.instance.getAllAccounts()[0];
    if (account && account.idTokenClaims) {
      return (account.idTokenClaims as any).roles || [];
    }

    return this._bst.roles || [];
  }
  getCustomAccessToken(): string | null {
    return this._bst.token;
  }

  getCustomRefreshToken(): string | null {
    return this._bst.refreshToken;
  }

  isCustomAccessTokenExpired(): boolean | null {
    return this._bst.isTokenExpired;
  }

  isCustomRefreshTokenExpired(): boolean | null {
    return this._bst.isRefreshTokenExpired;
  }

  getCustomLoginStatus(): Observable<{ status: boolean, eventType: EventType | null }> {
    this._iscustomLoggedIn.next({ status: (!!this.getCustomAccessToken() && !this.isCustomAccessTokenExpired()), eventType: null });
    return this._iscustomLoggedIn;
  }

  logout(url: string | null): void {

    // If local login only logout from local
    if (this._bst.token) {
      this._bst.clearAuthentication();
      this._iscustomLoggedIn.next({ status: false, eventType: null });
      this._router.navigate(['login', { url: url }]);
      return;
    }

    // Clear MSAL session
    if (this._authService.instance.getAllAccounts().length > 0) {
      this._authService.logoutPopup({
        postLogoutRedirectUri: "/auth",
        mainWindowRedirectUri: "/login"
      });
      this._router.navigate(['login', { url: url }]);
    }

    if (this._authService.instance.getAllAccounts().length === 0) {
      this.setLogedInStatus(false);
      this._router.navigate(['login', { url: url }]);
    }
  }

  isUserAuthenticated(): Observable<boolean> {
    return this.isLoggedIn$;
  }
}